import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { getHumanReadableGrouping, invoiceHitWithComputedStatus } from '../../helpers/functions';
import { useSourSearchContext } from '../helpers/useSourSearch';
import { CustomerResult } from './components/CustomerResult';
import { DumpsterResult } from './components/DumpsterResult';
import { HaulerResult } from './components/HaulerResult';
import { LeadResult } from './components/LeadResult';
import { OrderResult } from './components/OrderResult';
import { PayableResult } from './components/PayableResult';
import { ReceivableResult } from './components/ReceivableResult';
import { ServiceGroupingResult } from './components/ServiceGroupingResult';
import { ZoneResult } from './components/ZoneResults';
import { TaggedContainerResult } from './components/tagged-container-result';
export const SearchResults = ({ hits, hitsType, search, size }) => {
    const [groupedHits, setGroupedHits] = useState({});
    const { onSeeAllClick } = useSourSearchContext();
    // Order of indexes to display for aggregate results
    const aggregateOrder = [
        'customer-staging',
        'customer-production',
        'wayste-order-staging',
        'wayste-order-production',
        'sourgum-service-grouping-staging',
        'sourgum-service-grouping-production',
        'sourgum-payable-staging',
        'sourgum-payable-production',
        'sourgum-receivable-staging',
        'sourgum-receivable-production',
        'wayste-receivable-staging',
        'wayste-receivable-production',
        'sourgum-lead-staging',
        'sourgum-lead-production',
        'sourgum-vendor-staging',
        'sourgum-vendor-production',
        'wayste-vendor-staging',
        'wayste-vendor-production',
        'sourgum-pricing-zone-staging',
        'sourgum-pricing-zone-production',
        'wayste-pricing-zone-staging',
        'wayste-pricing-zone-production',
        'wayste-dumpster-staging',
        'wayste-dumpster-production',
    ];
    const groupByIndex = (hits) => {
        const groupedResults = hits.reduce((acc, hit) => {
            const index = hit._index;
            acc[index] = acc[index] || [];
            acc[index].push(hit);
            return acc;
        }, {});
        return groupedResults;
    };
    const groupByIndexInAgg = (hits) => {
        const groupedResults = hits.reduce((acc, hit) => {
            const index = hit.key;
            acc[index] = acc[index] || [];
            acc[index].push(...hit.hits_per_index.hits.hits);
            return acc;
        }, {});
        return groupedResults;
    };
    ///////////////////////////////////////////////
    // HOOKS
    ///////////////////////////////////////////////
    useEffect(() => {
        if (hitsType === 'general') {
            const groupedResults = groupByIndex(hits);
            setGroupedHits(groupedResults);
        }
        if (hitsType === 'aggregate') {
            const groupedResults = groupByIndexInAgg(hits);
            setGroupedHits(groupedResults);
        }
    }, [hits]);
    ///////////////////////////////////////////////
    // RENDER
    ///////////////////////////////////////////////
    if (!hits) {
        return null;
    }
    if (!groupedHits) {
        return null;
    }
    return (_jsxs("div", { className: `flex flex-col ${size ? size : 'xs:w-[90vw] md:w-[90vw] lg:w-[70vw]'} `, children: [_jsxs("div", { className: "text-sourgum-greyblue-500 border-sourgum-greyblue-50 border-b px-1 pt-1", onClick: () => onSeeAllClick && onSeeAllClick(search), children: ["show all results for: ", _jsx("span", { className: "font-semibold", children: search })] }), _jsx("div", { className: "px-1", children: groupedHits &&
                    Object.keys(groupedHits)
                        .sort((a, b) => {
                        return aggregateOrder.indexOf(a) - aggregateOrder.indexOf(b);
                    })
                        .map((index) => {
                        return (_jsxs(_Fragment, { children: [_jsx("div", { className: "text-sourgum-greyblue-500 px-1 pt-1 text-sm", children: getHumanReadableGrouping(index) }), groupedHits[index].map((hit) => {
                                    ///////////////////////////////////////////////
                                    // ORDER
                                    ///////////////////////////////////////////////
                                    if (hit._index.includes('sourgum-order') || hit._index.includes('wayste-order')) {
                                        return _jsx(OrderResult, { order: hit._source }, hit._id);
                                    }
                                    ///////////////////////////////////////////////
                                    // Service Grouping
                                    ///////////////////////////////////////////////
                                    if (hit._index.includes('sourgum-service-grouping')) {
                                        return _jsx(ServiceGroupingResult, { grouping: hit._source }, hit._id);
                                    }
                                    ///////////////////////////////////////////////
                                    // CUSTOMER
                                    ///////////////////////////////////////////////
                                    if (hit._index.includes('customer')) {
                                        return _jsx(CustomerResult, { customer: hit._source, hit: hit }, hit._id);
                                    }
                                    ///////////////////////////////////////////////
                                    // RECEIVABLE
                                    ///////////////////////////////////////////////
                                    if (hit._index.includes('sourgum-receivable') || hit._index.includes('wayste-receivable')) {
                                        return _jsx(ReceivableResult, { receivable: invoiceHitWithComputedStatus(hit) }, hit._id);
                                    }
                                    ///////////////////////////////////////////////
                                    // PAYABLE
                                    ///////////////////////////////////////////////
                                    if (hit._index.includes('sourgum-payable')) {
                                        return _jsx(PayableResult, { payable: invoiceHitWithComputedStatus(hit) }, hit._id);
                                    }
                                    ///////////////////////////////////////////////
                                    // LEAD
                                    ///////////////////////////////////////////////
                                    if (hit._index.includes('sourgum-lead')) {
                                        return _jsx(LeadResult, { lead: hit._source }, hit._id);
                                    }
                                    ///////////////////////////////////////////////
                                    // HAULER
                                    ///////////////////////////////////////////////
                                    if (hit._index.includes('sourgum-vendor') || hit._index.includes('wayste-vendor')) {
                                        return _jsx(HaulerResult, { hauler: hit._source }, hit._id);
                                    }
                                    ///////////////////////////////////////////////
                                    // PRICING ZONE
                                    ///////////////////////////////////////////////
                                    if (hit._index.includes('sourgum-pricing-zone') || hit._index.includes('wayste-pricing-zone')) {
                                        return _jsx(ZoneResult, { zone: hit._source, hit: hit }, hit._id);
                                    }
                                    ///////////////////////////////////////////////
                                    // DUMPSTER
                                    ///////////////////////////////////////////////
                                    if (hit._index.includes('wayste-dumpster')) {
                                        return _jsx(DumpsterResult, { dumpster: hit._source }, hit._id);
                                    }
                                    ///////////////////////////////////////////////
                                    // TAGGED CONTAINER
                                    ///////////////////////////////////////////////
                                    if (hit._index.includes('tagged-container')) {
                                        return _jsx(TaggedContainerResult, { container: hit._source }, hit._id);
                                    }
                                })] }));
                    }) })] }));
};
//# sourceMappingURL=searchResults.js.map